import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import useSWR from 'swr'
import useSWRInfinite from 'swr/infinite'
import { useCurrentTenantId } from '@/components/tenant/tenant-context'
import {
  getGuestHistory,
  getGuestRecord,
  GuestsRequestError,
  listGuests,
  searchGuests,
  type GuestHistoryPage,
  type GuestListId,
  type GuestListPage,
  type GuestRecord,
  type GuestSearchResult,
} from '@/lib/bff/guests'

const swrOptions = { revalidateOnFocus: false, shouldRetryOnError: false } as const

/** Every guest SWR key starts with this, then the tenant id, so a tenant switch asks again. */
const PREFIX = 'bff/guests'

function useToken() {
  const { getAccessTokenSilently } = useAuth0()
  return React.useCallback(async () => {
    const token = await getAccessTokenSilently()
    if (!token) throw new GuestsRequestError(401, null, [], 'No access token available')
    return token
  }, [getAccessTokenSilently])
}

/*
 * One page of a guest list of the current tenant. Pass `list: null` to make
 * no request. `date` is an Arrivals day, "YYYY-MM-DD"; leave it out for the
 * tenant's today. Every key asks again, and nothing is returned while its
 * request is in flight, so a page shown always comes from its own request:
 * for VisitOne staff each one is its own recorded opening (VIS-330, VIS-490).
 */
export function useGuestList(list: GuestListId | null, page: number, venue: string, date?: string) {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { data, error, isLoading, isValidating, mutate } = useSWR<GuestListPage>(
    list ? [PREFIX, tenantId, 'list', list, date ?? 'today', venue, page] : null,
    async ([, tenant, , id, day, v, p]: [string, string, string, GuestListId, string, string, number]) =>
      listGuests(await token(), tenant, id, p, { venue: v, date: day === 'today' ? undefined : day }),
    { ...swrOptions, dedupingInterval: 0, revalidateIfStale: true },
  )
  return {
    page: isValidating ? undefined : data,
    error: isValidating ? undefined : error,
    isLoading,
    revalidate: () => mutate(),
  }
}

/* The guests matching an already-validated term; `null` makes no request. The term is held in memory only. */
export function useGuestSearch(term: string | null) {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { data, error, isLoading, mutate } = useSWR<GuestSearchResult>(
    term ? [PREFIX, tenantId, 'search', term] : null,
    async ([, tenant, , q]: [string, string, string, string]) => searchGuests(await token(), tenant, q),
    swrOptions,
  )
  return { result: data, error, isLoading, revalidate: () => mutate() }
}

export function useGuestRecord(guestId: string) {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { data, error, isLoading, isValidating, mutate } = useSWR<GuestRecord>(
    [PREFIX, tenantId, 'record', guestId],
    async ([, tenant, , id]: [string, string, string, string]) => getGuestRecord(await token(), tenant, id),
    swrOptions,
  )
  return { record: data, error, isLoading, isValidating, revalidate: () => mutate() }
}

/*
 * The guest's history, page by page from newest. `loadEarlier` asks for the
 * next older page; a failure of that page keeps the pages already shown.
 * Nothing is asked for until the record has loaded and named its recorded
 * `view` (VIS-330), which every page passes on.
 */
export function useGuestHistory(guestId: string, view: string | undefined) {
  const token = useToken()
  const tenantId = useCurrentTenantId()
  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<GuestHistoryPage>(
    (index: number, previous: GuestHistoryPage | null) => {
      if (!view) return null
      if (index > 0 && !previous?.next) return null
      return [PREFIX, tenantId, 'history', guestId, index === 0 ? '' : (previous?.next ?? '')]
    },
    async ([, tenant, , id, before]: [string, string, string, string, string]) =>
      getGuestHistory(await token(), tenant, id, before || undefined, view),
    { ...swrOptions, revalidateFirstPage: false },
  )
  const pages = data ?? []
  const last = pages.at(-1)
  return {
    pages,
    error,
    isLoading,
    /** An older page was asked for and hasn't arrived. */
    loadingEarlier: size > pages.length && isValidating,
    hasEarlier: Boolean(last?.next),
    loadEarlier: () => void setSize(pages.length + 1),
    revalidate: () => mutate(),
  }
}
